import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  UserPlus, Bug, User, Users, Edit, Plus, Loader2, Search, 
  Skull, BookOpen, ExternalLink 
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { getCreatureStatByRole, getCreatureStatFields } from '../../lib/creatureStats';

export function AddCombatantDialogs({
  language,
  players,
  npcs,
  creatures,
  combatantPlayerIds,
  combatantNpcIds,
  monsterSearch,
  monsterResults,
  searchLoading,
  onAddPlayer,
  onAddNPC,
  onAddCreature,
  onAddCustomCombatant,
  onMonsterSearchChange,
  onSearchMonsters,
  onAddMonsterFromSearch
}) {
  const [addCombatantOpen, setAddCombatantOpen] = useState(false);
  const [monsterSearchOpen, setMonsterSearchOpen] = useState(false);
  const [addCombatantTab, setAddCombatantTab] = useState('players');
  const [newCombatant, setNewCombatant] = useState({
    name: '', combatant_type: 'monster', initiative: 0, initiative_modifier: 0,
    ac: 10, max_hp: 10, current_hp: 10, temp_hp: 0
  });

  const handleAddCustom = () => {
    if (!newCombatant.name) return;
    onAddCustomCombatant(newCombatant);
    setNewCombatant({
      name: '', combatant_type: 'monster', initiative: 0, initiative_modifier: 0,
      ac: 10, max_hp: 10, current_hp: 10, temp_hp: 0
    });
    setAddCombatantOpen(false);
  };

  return (
    <div className="flex flex-wrap gap-2 mb-4">
      <Dialog open={addCombatantOpen} onOpenChange={setAddCombatantOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" data-testid="add-combatant-btn">
            <UserPlus className="mr-1 h-4 w-4" />
            {language === 'fr' ? 'Ajouter un combattant' : 'Add combatant'}
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-cinzel">
              {language === 'fr' ? 'Ajouter un combattant' : 'Add combatant'}
            </DialogTitle>
          </DialogHeader>
          
          <Tabs value={addCombatantTab} onValueChange={setAddCombatantTab}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="players">
                <User className="h-4 w-4 mr-1" />
                {language === 'fr' ? 'Joueurs' : 'Players'}
              </TabsTrigger>
              <TabsTrigger value="npcs">
                <Users className="h-4 w-4 mr-1" />
                PNJ
              </TabsTrigger>
              <TabsTrigger value="creatures">
                <Skull className="h-4 w-4 mr-1" />
                {language === 'fr' ? 'Créatures' : 'Creatures'}
              </TabsTrigger>
              <TabsTrigger value="custom">
                <Edit className="h-4 w-4 mr-1" />
                {language === 'fr' ? 'Manuel' : 'Custom'}
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="players" className="mt-4">
              <ScrollArea className="h-64">
                <div className="space-y-2">
                  {players.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      {language === 'fr' ? 'Aucun joueur dans la campagne' : 'No players in campaign'}
                    </p>
                  ) : (
                    players.map(member => {
                      const alreadyAdded = combatantPlayerIds.includes(member.user_id);
                      return (
                        <div
                          key={member.user_id}
                          className={cn(
                            "flex items-center justify-between p-3 rounded-lg border",
                            alreadyAdded ? "opacity-50 bg-muted" : "hover:bg-secondary cursor-pointer"
                          )}
                          onClick={() => !alreadyAdded && onAddPlayer(member)}
                        >
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
                              <User className="h-5 w-5" />
                            </div>
                            <div>
                              <p className="font-medium">{member.character_name || member.username}</p>
                              <p className="text-xs text-muted-foreground">{member.character_class || 'Classe non définie'}</p>
                            </div>
                          </div>
                          {alreadyAdded ? (
                            <Badge variant="secondary">{language === 'fr' ? 'Ajouté' : 'Added'}</Badge>
                          ) : (
                            <Plus className="h-4 w-4" />
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
            
            <TabsContent value="npcs" className="mt-4">
              <ScrollArea className="h-64">
                <div className="space-y-2">
                  {npcs.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      {language === 'fr' ? 'Aucun PNJ créé' : 'No NPCs created'}
                    </p>
                  ) : (
                    npcs.map(npc => {
                      const alreadyAdded = combatantNpcIds.includes(npc.id);
                      return (
                        <div
                          key={npc.id}
                          className={cn(
                            "flex items-center justify-between p-3 rounded-lg border",
                            alreadyAdded ? "opacity-50 bg-muted" : "hover:bg-secondary cursor-pointer"
                          )}
                          onClick={() => !alreadyAdded && onAddNPC(npc)}
                        >
                          <div>
                            <p className="font-medium">{npc.name}</p>
                            <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                              {npc.description || '-'}
                            </p>
                          </div>
                          {alreadyAdded ? (
                            <Badge variant="secondary">{language === 'fr' ? 'Ajouté' : 'Added'}</Badge>
                          ) : (
                            <Plus className="h-4 w-4" />
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="creatures" className="mt-4">
              <ScrollArea className="h-64">
                <div className="space-y-2">
                  {creatures.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      {language === 'fr'
                        ? 'Aucune créature dans l’encyclopédie'
                        : 'No creatures in the encyclopedia'}
                    </p>
                  ) : (
                    creatures.map(creature => {
                      const stats = getCreatureStatFields(creature.entity_data);
                      const health = getCreatureStatByRole(stats, 'health');
                      const defense = getCreatureStatByRole(stats, 'defense');
                      const rating = getCreatureStatByRole(stats, 'rating');
                      return (
                        <div
                          key={creature.id}
                          className="flex items-center justify-between gap-3 p-3 rounded-lg border hover:bg-secondary cursor-pointer"
                          onClick={() => onAddCreature(creature)}
                        >
                          <div className="flex min-w-0 items-center gap-3">
                            <div className="h-10 w-10 shrink-0 rounded-full bg-orange-500/20 flex items-center justify-center">
                              <Skull className="h-5 w-5 text-orange-500" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium truncate">{creature.title}</p>
                              <p className="text-xs text-muted-foreground truncate">
                                {[defense, health, rating]
                                  .filter(field => field?.value !== undefined && field?.value !== '')
                                  .map(field => `${field.label}: ${field.value}`)
                                  .join(' · ') || (language === 'fr' ? 'Statistiques libres' : 'Custom statistics')}
                              </p>
                            </div>
                          </div>
                          <Plus className="h-4 w-4 shrink-0" />
                        </div>
                      );
                    })
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
            
            <TabsContent value="custom" className="mt-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">{language === 'fr' ? 'Nom' : 'Name'}</Label>
                  <Input
                    value={newCombatant.name}
                    onChange={(e) => setNewCombatant(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Goblin"
                  />
                </div>
                <div>
                  <Label className="text-xs">{language === 'fr' ? 'Type' : 'Type'}</Label>
                  <Select 
                    value={newCombatant.combatant_type} 
                    onValueChange={(v) => setNewCombatant(prev => ({ ...prev, combatant_type: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monster">{language === 'fr' ? 'Monstre' : 'Monster'}</SelectItem>
                      <SelectItem value="npc">PNJ</SelectItem>
                      <SelectItem value="player">{language === 'fr' ? 'Joueur' : 'Player'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">{language === 'fr' ? 'Défense' : 'Defense'}</Label>
                  <Input
                    type="number"
                    value={newCombatant.ac}
                    onChange={(e) => setNewCombatant(prev => ({ ...prev, ac: parseInt(e.target.value) || 10 }))}
                  />
                </div>
                <div>
                  <Label className="text-xs">{language === 'fr' ? 'Santé / endurance' : 'Health / endurance'}</Label>
                  <Input
                    type="number"
                    value={newCombatant.max_hp}
                    onChange={(e) => {
                      const hp = parseInt(e.target.value) || 10;
                      setNewCombatant(prev => ({ ...prev, max_hp: hp, current_hp: hp }));
                    }}
                  />
                </div>
                <div>
                  <Label className="text-xs">{language === 'fr' ? 'Mod. Init.' : 'Init. Mod.'}</Label>
                  <Input
                    type="number"
                    value={newCombatant.initiative_modifier}
                    onChange={(e) => setNewCombatant(prev => ({ ...prev, initiative_modifier: parseInt(e.target.value) || 0 }))}
                  />
                </div>
              </div>
              <Button onClick={handleAddCustom} className="w-full" disabled={!newCombatant.name}>
                <Plus className="mr-2 h-4 w-4" />
                {language === 'fr' ? 'Ajouter' : 'Add'}
              </Button>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      <Dialog open={monsterSearchOpen} onOpenChange={setMonsterSearchOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" data-testid="search-monsters-btn">
            <Bug className="mr-1 h-4 w-4" />
            {language === 'fr' ? 'Bestiaire D&D 5e' : 'D&D 5e Bestiary'}
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-cinzel flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              {language === 'fr' ? 'Bestiaire D&D 5e (SRD)' : 'D&D 5e Bestiary (SRD)'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={monsterSearch}
                onChange={(e) => onMonsterSearchChange(e.target.value)}
                placeholder={language === 'fr' ? 'dragon, goblin, orc...' : 'dragon, goblin, orc...'}
                onKeyPress={(e) => e.key === 'Enter' && onSearchMonsters()}
              />
              <Button onClick={onSearchMonsters} disabled={searchLoading}>
                {searchLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>
            
            <ScrollArea className="h-80">
              <div className="space-y-2">
                {monsterResults.map(monster => (
                  <div
                    key={monster.index}
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-secondary cursor-pointer"
                    onClick={() => { onAddMonsterFromSearch(monster.index); setMonsterSearchOpen(false); }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-destructive/20 flex items-center justify-center">
                        <Skull className="h-5 w-5 text-destructive" />
                      </div>
                      <div>
                        <p className="font-medium">{monster.name}</p>
                        <a 
                          href={`https://www.aidedd.org/dnd/monstres.php?vo=${monster.index}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary flex items-center gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="h-3 w-3" />
                          AideDD
                        </a>
                      </div>
                    </div>
                    <Plus className="h-4 w-4" />
                  </div>
                ))}
                {monsterResults.length === 0 && monsterSearch && !searchLoading && (
                  <p className="text-center text-muted-foreground py-8">
                    {language === 'fr' ? 'Aucun résultat' : 'No results'}
                  </p>
                )}
                {!monsterSearch && (
                  <div className="text-center text-muted-foreground py-8">
                    <Bug className="h-12 w-12 mx-auto mb-2 opacity-30" />
                    <p>{language === 'fr' ? 'Recherchez un monstre' : 'Search for a monster'}</p>
                    <p className="text-xs mt-2">
                      {language === 'fr' 
                        ? 'Ex: dragon, zombie, wolf, giant...'
                        : 'Ex: dragon, zombie, wolf, giant...'}
                    </p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
